import { useIntl } from 'react-intl';
import * as React from 'react';
import { RootCategory, RootCategoryForm } from '@/components/BudgetCalculator/RootCategoryForm';
import { getSankeyData } from '@/components/BudgetCalculator/util';
import { Tabs } from '@/components/BudgetCalculator/Tabs';
import { ResponsiveContainer, Sankey } from 'recharts';
import { CustomNode } from '@/components/BudgetCalculator/CustomNode';
import { CustomLink } from '@/components/BudgetCalculator/CustomLink';
import { styled } from '../../../stitches.config';
import Button from '@/components/sushi/Button';
import { exportSvgToPng } from '@/utils/svg/svg';
import { useRouter } from 'next/router';
import { CopyButton } from '@/components/BudgetCalculator/CopyButton';
import * as $segment from '@/utils/segment';
import { CalculatorRecap } from '@/components/BudgetCalculator/CalculatorRecap';

/**
 * Serialize the data to as a query string
 */
const serializeData = (income: RootCategory, investments: RootCategory, expenses: RootCategory) => {
  return new URLSearchParams({
    income: JSON.stringify(income),
    investments: JSON.stringify(investments),
    expenses: JSON.stringify(expenses),
  });
};

/**
 * Get the stateful shareable url of the document
 */
const getDocumentShareableUrl = (
  income: RootCategory,
  investments: RootCategory,
  expenses: RootCategory
) => {
  const urlSearchParams = serializeData(income, investments, expenses);
  const base = window.location.href.split('?')[0];
  return `${base}?${urlSearchParams}`;
};

/**
 * Deserializes the state from the current query string
 */
const useDeserializedValues = () => {
  const { query } = useRouter();
  const { income: incomeQuery, investments: investmentsQuery, expenses: expensesQuery } = query;
  const deserializedIncome = React.useMemo(() => {
    if (incomeQuery) {
      return JSON.parse(incomeQuery as string);
    }
  }, [incomeQuery]);

  const deserializedInvestments = React.useMemo(() => {
    if (investmentsQuery) {
      return JSON.parse(investmentsQuery as string);
    }
  }, [investmentsQuery]);

  const deserializedExpenses = React.useMemo(() => {
    if (expensesQuery) {
      return JSON.parse(expensesQuery as string);
    }
  }, [expensesQuery]);
  return { deserializedIncome, deserializedInvestments, deserializedExpenses };
};

const useDefaultValues = () => {
  const { formatMessage } = useIntl();

  const defaultIncome = {
    id: '06a35e37-6475-45ae-97a5-e091f60c31a3',
    name: formatMessage({ id: 'budget_calculator.tabs.income' }),
    i18nKey: 'income',
    categories: [
      {
        id: '2d69023b-e4d7-4884-9c8e-529c17356d63',
        name: formatMessage({ id: 'budget_calculator.default_values.income.title' }),
        values: [
          {
            id: '94b13a7c-6cde-47db-87db-a0d4b24718b4',
            name: formatMessage({ id: 'budget_calculator.default_values.income.salary' }),
            value: '2500',
          },
        ],
      },
    ],
  };

  const defaultInvestments = {
    id: '6a0c5ee1-37b0-4a7f-bbb2-084f2ecb9056',
    name: formatMessage({ id: 'budget_calculator.tabs.investments' }),
    i18nKey: 'investments',
    categories: [
      {
        id: '4dbb3e83-d131-4023-a3ab-c1ebdb37e580',
        name: formatMessage({ id: 'budget_calculator.default_values.investments.title' }),
        values: [
          {
            id: 'd9a8f61d-b4fc-4659-b4fa-4cb4bcb794aa',
            name: formatMessage({ id: 'budget_calculator.default_values.investments.stocks' }),
            value: '200',
          },
          {
            id: '44dbdac1-df94-48ba-83bd-6f17ed813d47',
            name: formatMessage({
              id: 'budget_calculator.default_values.investments.life_insurance',
            }),
            value: '200',
          },
        ],
      },
    ],
  };

  const defaultExpenses = {
    id: 'c5880ce5-8636-42ad-b146-44c73b7e27a2',
    name: formatMessage({ id: 'budget_calculator.tabs.expenses' }),
    i18nKey: 'expenses',
    categories: [
      {
        id: '4a925bdb-4312-483c-9785-3ed6520f0f12',
        name: formatMessage({ id: 'budget_calculator.default_values.expenses.household.title' }),
        values: [
          {
            id: '683aa0c1-32ac-4123-a54b-9f0d95153f7a',
            name: formatMessage({ id: 'budget_calculator.default_values.expenses.household.rent' }),
            value: '500',
          },
          {
            id: '2118d6a8-0c6c-447a-9f91-5fe6a5be68e3',
            name: formatMessage({
              id: 'budget_calculator.default_values.expenses.household.charges',
            }),
            value: '120',
          },
        ],
      },
      {
        id: 'b7b3eea6-7377-4ef4-9085-5b1324d1d1bc',
        name: formatMessage({ id: 'budget_calculator.default_values.expenses.daily_life.title' }),
        values: [
          {
            id: 'd0269b6d-ac88-42a5-9fff-9be150c757d3',
            name: formatMessage({
              id: 'budget_calculator.default_values.expenses.daily_life.groceries',
            }),
            value: '300',
          },
          {
            id: '6fbd20ac-af16-40c6-8e39-dab272d0daf8',
            name: formatMessage({
              id: 'budget_calculator.default_values.expenses.daily_life.restaurants',
            }),
            value: '100',
          },
        ],
      },
      {
        id: '9239de09-d292-40dc-9069-d1fa717796ee',
        name: formatMessage({
          id: 'budget_calculator.default_values.expenses.subscriptions.title',
        }),
        values: [
          {
            id: 'f543a282-5dce-42b3-b0df-1329f33f5099',
            name: formatMessage({
              id: 'budget_calculator.default_values.expenses.subscriptions.internet_phone',
            }),
            value: '50',
          },
          {
            id: '6857b827-5dee-41bb-9951-67b70f0f4844',
            name: formatMessage({
              id: 'budget_calculator.default_values.expenses.subscriptions.sport',
            }),
            value: '20',
          },
        ],
      },
    ],
  };

  return { defaultIncome, defaultInvestments, defaultExpenses };
};

export const Calculator = () => {
  const { formatMessage } = useIntl();
  const svgContainerRef = React.useRef<HTMLDivElement>(null);
  const sankeyAndRecapRef = React.useRef<HTMLDivElement>(null);
  const { defaultIncome, defaultInvestments, defaultExpenses } = useDefaultValues();
  const [income, setIncome] = React.useState<RootCategory>(defaultIncome);
  const [investments, setInvestments] = React.useState<RootCategory>(defaultInvestments);
  const [expenses, setExpenses] = React.useState<RootCategory>(defaultExpenses);
  const { isReady } = useRouter();
  const { deserializedIncome, deserializedInvestments, deserializedExpenses } =
    useDeserializedValues();
  const loaded = React.useRef(false);
  const deserialized = React.useRef(false);

  // Compute sankey data
  const data = getSankeyData(
    income,
    investments,
    expenses,
    formatMessage({ id: 'budget_calculator.form.budget_label' })
  );

  // Move foreign objects to the top
  const moveForeignObjectsToTop = () => {
    const svg = svgContainerRef.current?.querySelector('svg');
    const foreignObjects = svg?.querySelectorAll('foreignObject');
    foreignObjects?.forEach((foreignObject) => {
      svg?.appendChild(foreignObject);
    });
  };

  // Move foreign objects to the top after each render
  React.useEffect(() => moveForeignObjectsToTop());

  React.useEffect(() => {
    $segment.event('simulator_started', { simulator: 'budget' });
  }, []);

  const resultsTrackedRef = React.useRef(false);
  React.useEffect(() => {
    if (deserialized.current && !resultsTrackedRef.current) {
      resultsTrackedRef.current = true;
      $segment.event('simulator_results_shown', { simulator: 'budget' });
    }
  }, [income, investments, expenses]);

  // Load deserialized data only on first load
  React.useEffect(() => {
    if (isReady) {
      if (!loaded.current) {
        loaded.current = true;
        if (deserializedIncome && deserializedInvestments && deserializedExpenses) {
          setIncome(deserializedIncome);
          setExpenses(deserializedExpenses);
          setInvestments(deserializedInvestments);
          deserialized.current = true;
        } else {
          setTimeout(() => {
            deserialized.current = true;
          });
        }
      }
    }
  }, [deserializedExpenses, deserializedIncome, deserializedInvestments, isReady]);

  // Update url with current data on each change after deserialization
  React.useEffect(() => {
    if (isReady && deserialized.current) {
      const url = getDocumentShareableUrl(income, investments, expenses);
      history.replaceState(undefined, undefined, url);
    }
  }, [expenses, income, investments, isReady]);

  const nodePadding = 16;
  const nodeMinVerticalSpace = 10;
  const investmentsEntriesCount = investments.categories.reduce(
    (acc, category) => acc + category.values.length,
    0
  );
  const expensesEntriesCount = expenses.categories.reduce(
    (acc, category) => acc + category.values.length,
    0
  );
  // when a user enters a lot of information we need to make the chart taller so that it doesn't overflow
  // and is still somewhat legible
  const sankeyHeight = Math.max(
    500,
    (investmentsEntriesCount + expensesEntriesCount) * (nodePadding + nodeMinVerticalSpace)
  );

  return (
    <>
      <Tabs
        tabs={[
          {
            label: formatMessage({ id: `budget_calculator.tabs.income` }),
            children: <RootCategoryForm values={income} setValues={setIncome} single />,
          },
          {
            label: formatMessage({ id: `budget_calculator.tabs.investments` }),
            children: <RootCategoryForm values={investments} setValues={setInvestments} />,
          },
          {
            label: formatMessage({ id: `budget_calculator.tabs.expenses` }),
            children: <RootCategoryForm values={expenses} setValues={setExpenses} />,
          },
        ]}
        Footer={({ tabs, activeTabIndex, setActiveTabIndex }) => {
          const isFirstTab = activeTabIndex === 0;
          const isLastTab = activeTabIndex === tabs.length - 1;
          return (
            <TabsFooter isFirstTab={isFirstTab}>
              {!isFirstTab ? (
                <Button
                  variant={'quaternaryDark'}
                  size={'small'}
                  onClick={() => setActiveTabIndex(activeTabIndex - 1)}>
                  {formatMessage({ id: `budget_calculator.tabs_footer.back` })}
                </Button>
              ) : null}
              <Button
                variant={'primaryLight'}
                size={'small'}
                onClick={() => {
                  if (isLastTab) {
                    sankeyAndRecapRef.current?.scrollIntoView({ behavior: 'smooth' });
                  } else {
                    setActiveTabIndex(activeTabIndex + 1);
                  }
                }}>
                {isLastTab
                  ? formatMessage({ id: `budget_calculator.tabs_footer.discover_cashflow` })
                  : formatMessage({ id: `budget_calculator.tabs_footer.next` })}
              </Button>
            </TabsFooter>
          );
        }}
      />
      <SankeyAndRecap ref={sankeyAndRecapRef}>
        <CalculatorRecap income={income} investments={investments} expenses={expenses} />
        <SankeyContainer ref={svgContainerRef}>
          <ResponsiveContainer
            width={'100%'}
            height={sankeyHeight}
            onResize={() => moveForeignObjectsToTop()}>
            <Sankey
              key={Math.random() /* force re-rendering from scratch to avoid having stale labels */}
              iterations={1}
              data={data}
              node={CustomNode}
              nodePadding={nodePadding}
              nodeWidth={20}
              linkCurvature={0.5}
              margin={{
                left: 20,
                right: 20,
                top: 40,
                bottom: 40,
              }}
              link={CustomLink}
            />
          </ResponsiveContainer>
          <SankeyFooter>
            <CopyButton
              onClick={() => {
                $segment.event('Website - Budget Calculator Sharing', {
                  budget_calculator_sharing_type: 'link sharing',
                });
              }}
              value={isReady ? getDocumentShareableUrl(income, investments, expenses) : ''}>
              {formatMessage({ id: `budget_calculator.share_results` })}
            </CopyButton>

            <Button
              variant={'secondaryLight'}
              size={'small'}
              onClick={() => {
                $segment.event('Website - Budget Calculator Sharing', {
                  budget_calculator_sharing_type: 'image sharing',
                });
                const svg = svgContainerRef.current?.querySelector('svg');
                if (svg) {
                  exportSvgToPng(svg, 'finary-cashflow', {
                    padding: 20,
                    addLogo: false,
                    backgroundColor: '#fff',
                  });
                }
              }}>
              {formatMessage({ id: `budget_calculator.export_chart` })}
            </Button>
          </SankeyFooter>
        </SankeyContainer>
      </SankeyAndRecap>
    </>
  );
};

const SankeyFooter = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  margin: '0 24px',
});

const TabsFooter = styled('div', {
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',

  variants: {
    isFirstTab: {
      true: {
        justifyContent: 'flex-end',
      },
    },
  },
});

const SankeyAndRecap = styled('div', {
  backgroundColor: '#fff',
  width: '100%',
  padding: '0 20',
  scrollMargin: 80,
});

const SankeyContainer = styled('div', {
  width: '100%',
  maxWidth: 1256,
  margin: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 32,
});
